// Annotation layer for the reader: selecting text, painting highlights, and
// the sidebar listing them.
//
// The DOM work lives in /shared/highlight.js and the matching in
// /shared/anchor.js; this file is the glue and the UI.
import { api } from './store.ts';
import type { Annotation } from '../../ui/shared/types.ts';
import { el } from '../../ui/shared/dom.ts';
import { locate, describeSelection, type SelectionAnchor } from '../../ui/shared/anchor.ts';
import {
  flatten, paintAnnotation, clearHighlights, selectionOffsets, rangeOffsets,
} from '../../ui/shared/highlight.ts';
import { scrollToPdfPage, isPdfPaged } from './pdf-viewer.ts';
import { isTextPaged, revealText } from './text-pager.ts';
import type { NoteEditor } from '@research/editor';

/**
 * Look up an element that the page is expected to contain.
 *
 * Throws rather than returning null: every id here is in index.html, so a
 * miss means the markup and the script have diverged — which is exactly the
 * failure that should be loud rather than a silent no-op further down.
 */
function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

/**
 * What highlights are painted into. An article is one continuous text; a PDF is
 * a stack of pages, each with its own text layer that only exists once that
 * page has been drawn — so a PDF's highlights are anchored to a page and
 * painted a page at a time.
 */
export type Surface = 'html' | 'pdf';

let surface: Surface = 'html';
let annotations: Annotation[] = [];
let bookmarkId: number | null = null;
/** The anchor for a live selection, awaiting a highlight/comment action. */
let pendingSelection: (SelectionAnchor & { page: number | null }) | null = null;
let onCountChange: ((count: number) => void) | null = null;
/** Highlights whose passage couldn't be found. For a PDF this fills in as
 *  pages are drawn, since an unseen page can't be checked. */
const orphaned = new Set<number>();

export function setSurface(next: Surface): void {
  surface = next;
}

const PDF_LAYER = '.pdf-text-layer';

export function setCountListener(fn: (count: number) => void): void {
  onCountChange = fn;
}

// ── Painting ──────────────────────────────────────────────────────────────────

/**
 * Repaint every annotation from scratch.
 *
 * Clearing first matters: painting splits text nodes, so incremental painting
 * would fragment the article a little more each time and eventually drift.
 */
export function repaint(): void {
  if (surface === 'pdf') {
    for (const layer of document.querySelectorAll<HTMLElement>(`#pdf-content ${PDF_LAYER}`)) {
      paintLayer(Number(layer.dataset.pageNumber), layer);
    }
    renderSidebar(null);
    onCountChange?.(annotations.length);
    return;
  }

  const root = $('reader-content');
  if (!root) return;

  clearHighlights(root);
  const { text, positions } = flatten(root);

  orphaned.clear();
  // Paint later annotations first: each paint mutates the DOM after its own
  // offset, and positions[] was captured before any of them ran.
  const located = annotations
    .map((annotation) => ({ annotation, at: locate(text, annotation) }))
    .sort((a, b) => (b.at.start ?? -1) - (a.at.start ?? -1));

  for (const { annotation, at } of located) {
    if (at.method === 'orphan') {
      orphaned.add(annotation.id);
      continue;
    }
    paintAnnotation(root, positions, annotation, at as { start: number; end: number; method: string });
  }

  renderSidebar(text);
  onCountChange?.(annotations.length);
}

/** Paint one text layer's worth of highlights. Returns whether any card's
 *  orphaned state changed, so the sidebar only redraws when it needs to. */
function paintLayer(page: number, layer: HTMLElement): boolean {
  clearHighlights(layer);

  const mine = annotations.filter((a) => a.page === page);
  if (!mine.length) return false;

  const { text, positions } = flatten(layer);
  const located = mine
    .map((annotation) => ({ annotation, at: locate(text, annotation) }))
    .sort((a, b) => (b.at.start ?? -1) - (a.at.start ?? -1));

  let changed = false;
  for (const { annotation, at } of located) {
    const wasOrphaned = orphaned.has(annotation.id);
    if (at.method === 'orphan') {
      orphaned.add(annotation.id);
    } else {
      orphaned.delete(annotation.id);
      paintAnnotation(layer, positions, annotation, at as { start: number; end: number; method: string });
    }
    if (wasOrphaned !== orphaned.has(annotation.id)) changed = true;
  }
  return changed;
}

/** A PDF page's text layer has just appeared (or reappeared): paint its highlights. */
export function paintPdfPage(page: number, layer: HTMLElement): void {
  if (surface !== 'pdf') return;
  if (paintLayer(page, layer)) renderSidebar(null);
}

// ── Selection toolbar ─────────────────────────────────────────────────────────

function hideToolbar(): void {
  $('selection-toolbar').hidden = true;
  pendingSelection = null;
}

function showToolbar(rect: DOMRect): void {
  const toolbar = $('selection-toolbar');
  toolbar.hidden = false;
  // Position above the selection, clamped into the viewport so a selection at
  // the very top of the article doesn't put the toolbar off-screen.
  const top = window.scrollY + rect.top - toolbar.offsetHeight - 8;
  toolbar.style.top = `${Math.max(window.scrollY + 8, top)}px`;
  const left = window.scrollX + rect.left + rect.width / 2 - toolbar.offsetWidth / 2;
  toolbar.style.left = `${Math.max(8, Math.min(left, window.innerWidth - toolbar.offsetWidth - 8))}px`;
}

/** The text layer a node sits in, if it's in one. */
function layerOf(node: Node): HTMLElement | null {
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return element?.closest<HTMLElement>(PDF_LAYER) ?? null;
}

/**
 * Where the current selection should be measured: the article for HTML, or the
 * text layer of the page it started on for a PDF. A drag across pages is
 * clipped to that first page — each highlight belongs to exactly one page —
 * and `rangeOffsets` does the clipping.
 */
function selectionTarget(
  selection: Selection,
): { root: HTMLElement; page: number | null; offsets: { start: number; end: number } | null } {
  if (surface === 'pdf') {
    const range = selection.getRangeAt(0);
    const layer = layerOf(range.startContainer) ?? layerOf(range.endContainer);
    return {
      root: layer ?? $('pdf-content'),
      page: layer ? Number(layer.dataset.pageNumber) : null,
      offsets: layer ? rangeOffsets(layer, range) : null,
    };
  }
  const root = $('reader-content');
  return { root, page: null, offsets: selectionOffsets(root, selection) };
}

function handleSelection(): void {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    hideToolbar();
    return;
  }

  const { root, page, offsets } = selectionTarget(selection);
  if (!offsets || (surface === 'pdf' && page === null)) {
    hideToolbar();
    return;
  }

  const { text } = flatten(root);
  pendingSelection = { ...describeSelection(text, offsets.start, offsets.end), page };

  // A selection landing entirely inside an existing highlight is almost always
  // a mis-click on the highlight itself rather than an intent to re-annotate.
  if (!pendingSelection.quote.trim()) {
    hideToolbar();
    return;
  }

  showToolbar(selection!.getRangeAt(0).getBoundingClientRect());
}

async function createAnnotation({ withComment }: { withComment: boolean }): Promise<void> {
  if (!pendingSelection || !bookmarkId) return;
  const anchor = pendingSelection;

  hideToolbar();
  window.getSelection()?.removeAllRanges();

  // A comment is written in the side panel first; the highlight is only
  // created when it's saved, so cancelling leaves nothing behind.
  if (withComment) {
    openCommentEditor({ draft: anchor });
    return;
  }

  try {
    const created = await api.addAnnotation(bookmarkId, { ...anchor, comment: null });
    annotations.push(created);
    repaint();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function copySelection(): Promise<void> {
  if (!pendingSelection) return;
  try {
    await navigator.clipboard.writeText(pendingSelection.quote);
  } catch {
    // Clipboard access can be denied; nothing useful to say about it.
  }
  hideToolbar();
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

/** Resolves with the element once it exists, or null after `timeout` ms. */
function waitFor<T extends Element>(find: () => T | null, timeout = 3000): Promise<T | null> {
  return new Promise((resolve) => {
    const started = performance.now();
    const check = (): void => {
      const found = find();
      if (found || performance.now() - started > timeout) resolve(found);
      else requestAnimationFrame(check);
    };
    check();
  });
}

async function scrollToAnnotation(annotation: Annotation): Promise<void> {
  const find = (): Element | null => document.querySelector(`mark[data-annotation-id="${annotation.id}"]`);

  // A PDF page that hasn't been drawn has no highlight in the DOM yet: go to
  // the page, and the highlight appears as its text layer is built. Paged,
  // the highlight can be in the DOM on a page that isn't showing.
  if (annotation.page != null && (!find() || isPdfPaged())) {
    scrollToPdfPage(annotation.page);
  }
  const mark = await waitFor(find);
  if (!mark) return;
  if (isTextPaged()) revealText(mark);
  else mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
  mark.classList.add('flash');
  setTimeout(() => mark.classList.remove('flash'), 1200);
}

// ── Comments ──────────────────────────────────────────────────────────────────
//
// Comments are Markdown, written in the same editor as an article's notes
// (@research/editor, compact toolbar) inside the highlight's card, and shown
// formatted. The editor module is the reader's lazy chunk; until it has
// loaded, or if it fails to, a comment is shown as plain text and edited in a
// plain textarea — the stored text is the same either way.

type EditorModule = typeof import('@research/editor');
let editorModule: EditorModule | null = null;
let editorLoading: Promise<EditorModule | null> | null = null;

function loadEditor(): Promise<EditorModule | null> {
  editorLoading ??= import('@research/editor')
    .then((module) => {
      editorModule = module;
      // Re-render so comments already on screen as plain text get formatted.
      renderSidebar(lastOrderText);
      return module;
    })
    .catch((err) => {
      console.warn('Rich-text comments unavailable, using plain text:', err);
      return null;
    });
  return editorLoading;
}

function commentView(markdown: string): HTMLElement {
  if (!editorModule) return el('div', { class: 'annotation-comment' }, markdown);
  const view = el('div', { class: 'annotation-comment is-rich' });
  view.append(editorModule.renderMarkdown(markdown));
  return view;
}

/** What a comment is being written for: a new highlight, or an existing one. */
type CommentTarget = { draft: SelectionAnchor & { page: number | null } } | { annotation: Annotation };

/**
 * The comment being written, if any. Its card is kept as it is across
 * re-renders of the panel (a PDF re-renders it as each page is drawn), so the
 * editor in it isn't torn down mid-sentence.
 */
let editing: {
  target: CommentTarget;
  card: HTMLElement;
  initial: string;
  editor: NoteEditor | null;
  textarea: HTMLTextAreaElement;
} | null = null;

/** The id a draft's card is rendered under; no saved annotation has it. */
const DRAFT_ID = -1;

function commentText(): string {
  if (!editing) return '';
  return (editing.editor ? editing.editor.getData() : editing.textarea.value).trim();
}

function openCommentEditor(target: CommentTarget): void {
  // Only one comment is open at a time; moving to another keeps what was typed.
  if (editing) void closeCommentEditor({ save: commentText() !== editing.initial });

  const annotation = 'annotation' in target ? target.annotation : null;
  const quote = annotation?.quote ?? ('draft' in target ? target.draft.quote : '');
  const page = annotation?.page ?? ('draft' in target ? target.draft.page : null);
  const initial = annotation?.comment ?? '';

  const textarea = el('textarea', {
    class: 'annotation-comment-input',
    rows: 3,
    placeholder: 'Your comment…',
    'aria-label': 'Comment',
  }, initial) as HTMLTextAreaElement;
  const card = el('div', {
    class: 'annotation-card is-editing',
    dataset: { id: String(annotation?.id ?? DRAFT_ID) },
    onkeydown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void closeCommentEditor({ save: true });
      } else if (event.key === 'Escape' && !(event.target as HTMLElement).closest('.note-link-form')) {
        event.preventDefault();
        event.stopPropagation();
        void closeCommentEditor({ save: false });
      }
    },
  }, [
    page != null && el('div', { class: 'annotation-page' }, `p. ${page}`),
    el('blockquote', {}, quote),
    textarea,
    el('div', { class: 'annotation-actions' }, [
      el('button', { class: 'btn-sm primary', type: 'button', onclick: () => closeCommentEditor({ save: true }) }, 'save'),
      el('button', { class: 'btn-sm', type: 'button', onclick: () => closeCommentEditor({ save: false }) }, 'cancel'),
      el('span', { class: 'annotation-hint' }, '⌘/Ctrl+Enter'),
    ]),
  ]);

  const state = { target, card, initial, editor: null as NoteEditor | null, textarea };
  editing = state;
  $('annotation-panel').classList.remove('collapsed');
  renderSidebar(lastOrderText);
  card.scrollIntoView({ block: 'nearest' });
  textarea.focus();

  void loadEditor().then(async (module) => {
    if (!module || editing !== state) return;
    const editor = await module.createNoteEditor(textarea, () => {}, { toolbar: 'compact', autofocus: true });
    // Saved, cancelled or replaced while the editor was being built.
    if (editing !== state) return void editor.destroy();
    state.editor = editor;
  }).catch((err) => console.warn('Rich-text comment editor unavailable, using plain text:', err));
}

/** Close the open comment, saving it or not. Safe to call with none open. */
async function closeCommentEditor({ save }: { save: boolean }): Promise<void> {
  const state = editing;
  if (!state) return;
  const text = commentText();
  editing = null;
  void state.editor?.destroy();

  const comment = text || null;
  const changed = text !== state.initial;
  if (!save || ('annotation' in state.target && !changed)) {
    renderSidebar(lastOrderText);
    return;
  }
  try {
    if ('draft' in state.target) {
      const id = bookmarkId;
      if (!id) return;
      const created = await api.addAnnotation(id, { ...state.target.draft, comment });
      if (bookmarkId === id) annotations.push(created);
    } else {
      const { annotation } = state.target;
      const updated = await api.updateAnnotation(annotation.id, { comment });
      annotations = annotations.map((a) => (a.id === annotation.id ? updated : a));
    }
    repaint();
  } catch (err) {
    alert((err as Error).message);
    renderSidebar(lastOrderText);
  }
}

async function removeAnnotation(annotation: Annotation): Promise<void> {
  try {
    await api.deleteAnnotation(annotation.id);
    annotations = annotations.filter((a) => a.id !== annotation.id);
    repaint();
  } catch (err) {
    alert((err as Error).message);
  }
}

/** What the panel was last ordered by, so it can be re-rendered on its own. */
let lastOrderText: string | null = null;

/** `articleText` orders an article's cards by where they fall in it; a PDF's
 *  (null) are ordered by page instead, since there's no single text to measure. */
function renderSidebar(articleText: string | null): void {
  lastOrderText = articleText;
  const panel = $('annotation-list');
  if (!panel) return;

  $('annotation-count').textContent = annotations.length ? `(${annotations.length})` : '';

  // A comment being written for a new highlight is a card of its own, in the
  // place the highlight will take.
  const draft = editing && 'draft' in editing.target
    ? { ...editing.target.draft, id: DRAFT_ID, comment: null } as unknown as Annotation
    : null;
  const listed = draft ? [...annotations, draft] : annotations;

  if (!listed.length) {
    panel.replaceChildren(
      el('div', { class: 'msg' }, 'Select any text in the article to highlight it or add a comment.')
    );
    return;
  }

  // Follow the article's order, not the order they were created in.
  const ordered = [...listed].sort((a, b) => {
    if (articleText === null) {
      return (a.page ?? 0) - (b.page ?? 0) || (a.position_hint ?? Infinity) - (b.position_hint ?? Infinity);
    }
    const aAt = locate(articleText, a);
    const bAt = locate(articleText, b);
    return (aAt.start ?? Infinity) - (bAt.start ?? Infinity);
  });

  panel.replaceChildren(...ordered.map((annotation) => {
    if (editing && Number(editing.card.dataset.id) === annotation.id) return editing.card;
    const isOrphaned = orphaned.has(annotation.id);

    return el('div', {
      class: `annotation-card${isOrphaned ? ' orphaned' : ''}`,
      dataset: { id: String(annotation.id) },
    }, [
      isOrphaned && el('div', { class: 'orphan-note' },
        annotation.page != null
          ? `This passage wasn't found on page ${annotation.page}.`
          : 'This passage is no longer in the article — the page may have changed.'),
      annotation.page != null && el('div', { class: 'annotation-page' }, `p. ${annotation.page}`),
      el('blockquote', {
        onclick: () => !isOrphaned && scrollToAnnotation(annotation),
      }, annotation.quote),
      annotation.comment && commentView(annotation.comment),
      el('div', { class: 'annotation-actions' }, [
        el('button', { class: 'btn-sm', type: 'button', onclick: () => openCommentEditor({ annotation }) },
          annotation.comment ? 'edit note' : 'add note'),
        el('button', { class: 'btn-sm danger', type: 'button', onclick: () => removeAnnotation(annotation) },
          'delete'),
      ]),
    ]);
  }));
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

export async function loadAnnotations(id: number): Promise<void> {
  bookmarkId = id;
  // Comments are shown formatted once the editor module is in; start it now.
  void loadEditor();
  annotations = [];
  orphaned.clear();
  try {
    annotations = await api.getAnnotations(id);
  } catch {
    // A failure here shouldn't stop the article rendering.
    annotations = [];
  }
  repaint();
}

export function unloadAnnotations(): void {
  // A comment still open is saved if it was changed, as the article's notes are.
  if (editing) void closeCommentEditor({ save: commentText() !== editing.initial });
  bookmarkId = null;
  annotations = [];
  orphaned.clear();
  hideToolbar();
}

export function annotationCount(): number {
  return annotations.length;
}

/** Highlight the current selection from a keyboard shortcut. */
export function highlightSelection(): void {
  handleSelection();
  if (pendingSelection) createAnnotation({ withComment: false });
}

// Wired once at module load; the reader view persists across articles.
document.addEventListener('mouseup', (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('#selection-toolbar')) return;
  // Let the browser finish updating the selection before reading it.
  setTimeout(handleSelection, 0);
});

document.addEventListener('selectionchange', () => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) hideToolbar();
});

$('sel-highlight').addEventListener('click', () => createAnnotation({ withComment: false }));
$('sel-comment').addEventListener('click', () => createAnnotation({ withComment: true }));
$('sel-copy').addEventListener('click', copySelection);

// Clicking a highlight in the article (or on a PDF page) jumps to its card in the sidebar.
function jumpToCard(event: MouseEvent): void {
  const mark = (event.target as HTMLElement).closest<HTMLElement>('mark[data-annotation-id]');
  if (!mark) return;
  const card = document.querySelector(`.annotation-card[data-id="${mark.dataset.annotationId}"]`);
  if (!card) return;
  $('annotation-panel').classList.remove('collapsed');
  card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  card.classList.add('flash');
  setTimeout(() => card.classList.remove('flash'), 1200);
}

$('reader-content').addEventListener('click', jumpToCard);
$('pdf-content').addEventListener('click', jumpToCard);
